/**
 * Item Add/Modify rules taken from WinForms frmDietech_Item_AddMod, kept out of the
 * page so they can be tested. The server applies the same rules (ItemDAL); these only
 * keep the page honest before a save.
 */
import type { FoodGroup } from '../../types/referenceData';

/**
 * Dietech_Items_Level: 1 view, 2 modify, 3 add, 4 delete (ucDietech_Items.vb:344-365,
 * frmUser_Roles_AddMod.vb:1170-1179). Not the generic LEVEL scale.
 */
export const ITEMS_LEVEL = { VIEW: 1, MODIFY: 2, ADD: 3, DELETE: 4 } as const;

/** tblItem.Category: the General tab's Category radio group. */
export const CATEGORY_OPTIONS = [
  { value: 1, label: 'Food' },
  { value: 2, label: 'Beverage' },
  { value: 3, label: 'Supplement' },
] as const;
export const SUPPLEMENT_CATEGORY = 3;

/** tblItem.Class: the General tab's Class radio group. */
export const CLASS_OPTIONS = [
  { value: 1, label: 'Cold' },
  { value: 2, label: 'Hot' },
] as const;

/** Text limits from the WinForms controls (and the tblItem column sizes). */
export const ITEM_LIMITS = {
  description: 24,
  menuDescription: 35,
  cookToTemp: 10,
  holdTemp: 10,
  coolAtTemp: 75,
  prepInstructions: 500,
  prepPullDays: 250,
} as const;

/** Default text colour for a new item: Black, the tblItem.TT_ItemColor default (-16777216). */
export const DEFAULT_ITEM_COLOR = -16777216;

/**
 * tblItem.AIMNoMatchType: what happens when none of the item's auto item modifications
 * match a resident's diet order (frmDietech_Item_AddMod.SetNoMatchMenus). "Use custom"
 * only works with a custom No Match rule, so it is offered only when one exists.
 */
export function aimNoMatchOptions(hasCustomNoMatchRule: boolean): { value: number; label: string }[] {
  const options = [
    { value: 0, label: 'Reset to item defaults' },
    { value: 1, label: 'Leave the item as is' },
  ];
  if (hasCustomNoMatchRule) options.push({ value: 2, label: 'Use the custom No Match rule' });
  return options;
}

function parentMap(groups: FoodGroup[]): Map<number, number | null> {
  return new Map(groups.map((g) => [g.pKey_Prd_Food_Group, g.fKey_Prd_Food_Group ?? null]));
}

/** Every ancestor of a food group, nearest first. Safe on a cycle. */
export function foodGroupAncestors(id: number, groups: FoodGroup[]): number[] {
  const parents = parentMap(groups);
  const seen = new Set<number>([id]);
  const out: number[] = [];
  let p = parents.get(id) ?? null;
  while (p != null && !seen.has(p)) {
    seen.add(p);
    out.push(p);
    p = parents.get(p) ?? null;
  }
  return out;
}

/**
 * Adds a food group the way the server will store it (IsParentFoodGroup): only the
 * most specific group in a branch is kept, so adding a sub-group drops its parents,
 * and adding a parent of a group already on the item changes nothing.
 */
export function addFoodGroup(current: number[], id: number, groups: FoodGroup[]): { ids: number[]; note?: string } {
  if (current.includes(id)) return { ids: current, note: 'That food group is already on this item.' };
  if (current.some((c) => foodGroupAncestors(c, groups).includes(id))) {
    return { ids: current, note: 'A more specific group under it is already on this item.' };
  }
  const ancestors = new Set(foodGroupAncestors(id, groups));
  return { ids: [...current.filter((c) => !ancestors.has(c)), id] };
}

/** "Fruit > Citrus > Orange". */
export function foodGroupPath(id: number, groups: FoodGroup[]): string {
  const byId = new Map(groups.map((g) => [g.pKey_Prd_Food_Group, g.name]));
  return [...foodGroupAncestors(id, groups).reverse(), id].map((g) => byId.get(g) ?? `#${g}`).join(' > ');
}

/**
 * The full path for a food group ("Entree/Meat > Pork > Bacon"), or `fallback`
 * (the server's leaf name) while the food groups are loading or when the group
 * is not in the list.
 */
export function foodGroupPathOr(id: number | null | undefined, groups: FoodGroup[], fallback: string): string {
  if (!id || !groups.some((g) => g.pKey_Prd_Food_Group === id)) return fallback;
  return foodGroupPath(id, groups);
}

export function sameIdSet(a: readonly number[], b: readonly number[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((x) => set.has(x));
}
